import { memo } from 'react';
import { useRouter } from 'next/router';

import { Box, Toast, useToast } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyEditV2WebApiHandler_parkingDescription } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { updateListingField } from '../../../../hooks/queries/listingDetailsCache';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';

import { useAuth } from '../../../AuthProvider';
import RMWInput from '../../../RMWInput';

const ParkingDescriptionField = () => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const initialValue = listingDetails?.buildingAttributes?.parkingDescription ?? '';

  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.POLICIES);

  const { mutateAsync: updateParkingDescription, error: updateParkingDescriptionError } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditV2WebApiHandler_parkingDescription,
    applyToCache: ({ postData }) => [
      updateListingField(
        'buildingAttributes',
        (current) => current && { ...current, parkingDescription: postData.parkingDescription ?? '' },
      ),
    ],
    successToastMessage: 'Parking description updated',
    meta: {
      context: { api: 'RentalManagerUserPropertyEditV2WebApiHandler_parkingDescription', details: { listingAlias } },
    },
  });

  const handleBlur = async (event: string | { target: { name: string; value: string } }) => {
    const inputValue = typeof event === 'string' ? event : event.target.value;

    // Don't save if value hasn't changed
    if (inputValue === initialValue) {
      return;
    }

    try {
      await updateParkingDescription({
        postData: { parkingDescription: inputValue, listingAlias },
      });
    } catch (err) {
      const errorMessage = DatarouterError.getDisplayMessage(err, 'Failed to save parking description');
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: { lg: '1 1 50%', base: 'auto' } }}>
      <RMWInput
        disabled={isInputFormDisabled}
        errorMessage={(updateParkingDescriptionError as DatarouterError)?.message ?? ''}
        fieldName="parkingDescription"
        initialValue={initialValue}
        inputType="textarea"
        label="Additional parking description"
        maxLength="130"
        onBlur={handleBlur}
        showCharacterCount
      />
    </Box>
  );
};

export default memo(ParkingDescriptionField);
